/**
 * Utility functions for safe API response handling
 *
 * This module provides helper functions to safely parse API responses
 * and handle cases where the server returns HTML instead of JSON
 * (e.g., error pages, CloudFront errors, etc.)
 */

import { getApiUrl } from '@/config/api';

/**
 * Interface for parsed API response
 */
export interface ParsedResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  statusCode?: number;
  shouldRetry?: boolean;  // Flag for retry logic when HTML response detected
}

/**
 * Refresh the JWT access token using the refresh token
 *
 * @returns The new access token or null if refresh failed
 */
async function refreshAccessToken(): Promise<string | null> {
  try {
    const refreshToken = localStorage.getItem('refresh');

    if (!refreshToken) {
      console.error('No refresh token available');
      return null;
    }

    const response = await fetch(getApiUrl('/api/token/refresh/'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh: refreshToken }),
    });

    if (!response.ok) {
      console.error('Token refresh failed:', response.status);
      return null;
    }

    const data = await response.json();
    const newAccessToken = data.access;

    if (newAccessToken) {
      localStorage.setItem('access', newAccessToken);
      console.log('Access token refreshed successfully');
      return newAccessToken;
    }

    return null;
  } catch (error) {
    console.error('Error refreshing token:', error);
    return null;
  }
}

/**
 * Safely parse a Response object as JSON, handling HTML responses gracefully
 *
 * @param response - The fetch Response object
 * @param context - Optional context string for better error messages
 * @returns Parsed response with success status and data/error
 *
 * @example
 * ```typescript
 * const response = await fetch('/api/endpoint');
 * const result = await safeJsonParse(response, 'fetching user data');
 *
 * if (!result.success) {
 *   console.error('API Error:', result.error);
 *   alert(result.error);
 *   return;
 * }
 *
 * // Use result.data safely
 * console.log(result.data);
 * ```
 */
export async function safeJsonParse<T = any>(
  response: Response,
  context: string = 'API request'
): Promise<ParsedResponse<T>> {
  const statusCode = response.status;

  // Check if response is OK (2xx status)
  if (!response.ok) {
    // Check content type before trying to parse
    const contentType = response.headers.get('content-type');
    let errorMessage = `HTTP ${statusCode}: ${response.statusText}`;

    if (contentType && contentType.includes('application/json')) {
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.detail || errorData.message || errorMessage;
        console.error(`Error in ${context}:`, errorData);

        return {
          success: false,
          error: errorMessage,
          statusCode,
        };
      } catch (parseError) {
        console.error(`Failed to parse error response as JSON for ${context}:`, parseError);
        return {
          success: false,
          error: `${errorMessage} (Failed to parse error details)`,
          statusCode,
        };
      }
    } else {
      // Response is not JSON (likely HTML error page from CloudFront, ALB, etc.)
      const textResponse = await response.text();
      const preview = textResponse.substring(0, 200);
      console.error(`Non-JSON error response for ${context}:`, preview);

      // Provide user-friendly message based on status code
      if (statusCode === 404) {
        errorMessage = 'The requested resource was not found. Please try again or contact support.';
      } else if (statusCode === 500 || statusCode === 502 || statusCode === 503) {
        errorMessage = 'The server encountered an error. Please try again in a few moments.';
      } else if (statusCode === 401) {
        errorMessage = 'Authentication required. Please log in again.';
      } else if (statusCode === 403) {
        errorMessage = 'Access denied. You do not have permission for this action.';
      }

      return {
        success: false,
        error: errorMessage,
        statusCode,
      };
    }
  }

  // Response is OK, try to parse as JSON
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    const textResponse = await response.text();
    const lowerText = textResponse.trim().toLowerCase();
    const isHtmlResponse = lowerText.startsWith('<!doctype') ||
                           lowerText.startsWith('<html');

    // Log for debugging
    if (isHtmlResponse) {
      console.error(`Received HTML instead of JSON for ${context} - possible CloudFront routing issue`);
    } else {
      console.error(`Unexpected response format for ${context}:`, textResponse.substring(0, 200));
    }

    return {
      success: false,
      error: isHtmlResponse
        ? 'Service temporarily unavailable. Please try again.'
        : 'Server returned an unexpected response format.',
      statusCode,
      shouldRetry: isHtmlResponse, // Flag for retry logic
    };
  }

  try {
    const data = await response.json();
    return {
      success: true,
      data: data as T,
      statusCode,
    };
  } catch (parseError) {
    console.error(`Failed to parse successful response as JSON for ${context}:`, parseError);
    return {
      success: false,
      error: 'Failed to parse server response. Please try again.',
      statusCode,
    };
  }
}

/**
 * Make a fetch request with automatic JSON parsing and error handling
 *
 * @param url - The URL to fetch
 * @param options - Fetch options (headers, method, body, etc.)
 * @param context - Optional context string for better error messages
 * @returns Parsed response with success status and data/error
 *
 * @example
 * ```typescript
 * const result = await safeFetch<UserData>(
 *   '/api/user/profile',
 *   {
 *     method: 'GET',
 *     headers: { 'Authorization': `Bearer ${token}` }
 *   },
 *   'fetching user profile'
 * );
 *
 * if (!result.success) {
 *   alert(result.error);
 *   return;
 * }
 *
 * setUserData(result.data);
 * ```
 */
export async function safeFetch<T = any>(
  url: string,
  options?: RequestInit,
  context: string = 'API request'
): Promise<ParsedResponse<T>> {
  try {
    const response = await fetch(url, options);
    return await safeJsonParse<T>(response, context);
  } catch (error) {
    // Network error or other fetch failure
    console.error(`Network error during ${context}:`, error);

    const errorMessage = error instanceof Error
      ? error.message
      : 'Network error. Please check your connection and try again.';

    return {
      success: false,
      error: errorMessage,
    };
  }
}

/**
 * Make an authenticated fetch request with automatic token refresh on 401 errors
 *
 * This function automatically handles JWT token expiration by:
 * 1. Making the initial request with the access token
 * 2. If 401 is returned, attempting to refresh the token
 * 3. Retrying the request with the new token
 * 4. Redirecting to login if refresh fails
 *
 * @param url - The URL to fetch
 * @param options - Fetch options (will be merged with Authorization header)
 * @param context - Optional context string for better error messages
 * @returns Parsed response with success status and data/error
 *
 * @example
 * ```typescript
 * const result = await authenticatedFetch<Team[]>(
 *   getApiUrl('/api/teams/teams/'),
 *   { method: 'GET' },
 *   'fetching teams'
 * );
 *
 * if (!result.success) {
 *   console.error('Error:', result.error);
 *   return;
 * }
 *
 * setTeams(result.data);
 * ```
 */
export async function authenticatedFetch<T = any>(
  url: string,
  options: RequestInit = {},
  context: string = 'authenticated API request'
): Promise<ParsedResponse<T>> {
  // Get access token from localStorage
  let accessToken = localStorage.getItem('access');

  if (!accessToken) {
    console.error('No access token available');
    // Redirect to login
    window.location.href = '/login';
    return {
      success: false,
      error: 'Authentication required. Please log in.',
    };
  }

  // Add Authorization header to options
  const authOptions: RequestInit = {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${accessToken}`,
    },
  };

  // Make the initial request
  try {
    let response = await fetch(url, authOptions);

    // If 401 (Unauthorized), try to refresh the token and retry
    if (response.status === 401) {
      console.log(`${context}: Got 401, attempting token refresh...`);

      const newAccessToken = await refreshAccessToken();

      if (!newAccessToken) {
        console.error(`${context}: Token refresh failed, redirecting to login`);
        // Redirect to login
        window.location.href = '/login';
        return {
          success: false,
          error: 'Session expired. Please log in again.',
          statusCode: 401,
        };
      }

      // Retry the request with the new token
      authOptions.headers = {
        ...authOptions.headers,
        'Authorization': `Bearer ${newAccessToken}`,
      };

      response = await fetch(url, authOptions);

    }

    return await safeJsonParse<T>(response, context);
  } catch (error) {
    // Network error or other fetch failure
    console.error(`Network error during ${context}:`, error);

    const errorMessage = error instanceof Error
      ? error.message
      : 'Network error. Please check your connection and try again.';

    return {
      success: false,
      error: errorMessage,
    };
  }
}

/**
 * Retry logic for API calls that might hit CloudFront routing issues
 *
 * This function automatically retries requests that receive HTML instead of JSON,
 * which can happen due to CloudFront routing misconfiguration.
 *
 * @param url - The URL to fetch
 * @param options - Fetch options (will be merged with Authorization header)
 * @param context - Optional context string for better error messages
 * @param maxRetries - Maximum number of retry attempts (default: 2)
 * @returns Parsed response with success status and data/error
 *
 * @example
 * ```typescript
 * const result = await authenticatedFetchWithRetry<ReadinessResponse>(
 *   getApiUrl('/api/readiness/?days=14'),
 *   { method: 'GET' },
 *   'fetching readiness data',
 *   2
 * );
 * ```
 */
export async function authenticatedFetchWithRetry<T = any>(
  url: string,
  options: RequestInit = {},
  context: string = 'authenticated API request',
  maxRetries: number = 2
): Promise<ParsedResponse<T>> {
  let lastError: ParsedResponse<T> | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const result = await authenticatedFetch<T>(url, options, context);

    // Success - return immediately
    if (result.success) {
      return result;
    }

    // Don't retry if it's a legitimate error (not HTML response)
    if (!result.shouldRetry || attempt === maxRetries) {
      return result;
    }

    // Wait before retry (exponential backoff: 500ms, 1000ms)
    lastError = result;
    const delay = 500 * (attempt + 1);
    console.log(`Retrying ${context} in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);
    await new Promise(resolve => setTimeout(resolve, delay));
  }

  return lastError!;
}
